import React, { useState, useEffect, useRef } from 'react';
import { copyToClipboard } from '@/shared/utils/exportUtils';

export default function AdminTerminalCommandsSection({ appId, showToast }) {
  const [copiedCommand, setCopiedCommand] = useState(null);
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);
  const infoRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoRef.current && !infoRef.current.contains(event.target)) {
        setActiveInfoPopup(null);
      }
    };

    if (activeInfoPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeInfoPopup]);

  const commands = [
    {
      label: 'Open in Cursor',
      command: `cursor ~/zapt-apps/${appId}`,
      description: 'Open this project in Cursor editor',
      requiresSetup: true,
      setupIntro: 'You need to install the Cursor CLI command first. Here\'s how:',
      setupInstructions: [
        'Open Cursor (the application)',
        'Press Cmd+Shift+P to open the Command Palette',
        'Type: "Shell Command: Install \'cursor\' command in PATH"',
        'Select it and hit Enter'
      ]
    },
    {
      label: 'Navigate to project',
      command: `cd ~/zapt-apps/${appId}`,
      description: 'Change directory to this project'
    }
  ];

  const handleCopy = async (command, label) => {
    const result = await copyToClipboard(command);
    if (result.success) {
      setCopiedCommand(label);
      if (showToast) {
        showToast(`Copied: ${command}`, 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy command', 'error');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>💻</span>
          <span>Terminal Commands</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Quick access commands for working with this project
        </p>
      </div>

      <div className="space-y-3">
        {commands.map(({ label, command, description, requiresSetup, setupIntro, setupInstructions }) => (
          <div
            key={label}
            className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-900">{label}</p>
                  {requiresSetup && (
                    <div className="relative" ref={activeInfoPopup === label ? infoRef : null}>
                      <button
                        onClick={() => setActiveInfoPopup(activeInfoPopup === label ? null : label)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Show setup instructions"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      {activeInfoPopup === label && (
                        <div className="absolute left-0 top-6 z-10 w-80 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-900">Setup Required</h4>
                            <button
                              onClick={() => setActiveInfoPopup(null)}
                              className="text-gray-400 hover:text-gray-600"
                              aria-label="Close"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 mb-3">
                            {setupIntro}
                          </p>
                          <ol className="text-xs text-gray-700 space-y-2 list-decimal list-inside">
                            {setupInstructions?.map((instruction, index) => (
                              <li key={index} className="pl-1">{instruction}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-2">{description}</p>
                <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block break-all">
                  {command}
                </code>
              </div>
              <button
                onClick={() => handleCopy(command, label)}
                className="ml-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 flex-shrink-0"
              >
                {copiedCommand === label ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

